import { DownloadOutlined } from '@ant-design/icons'
import { message, notification, Space, Table } from 'antd'
import { Button, ButtonActionBar, Typography } from 'modules/core/components'
import { calculateScrollX } from 'modules/core/utils'
import { useTranslation } from 'react-i18next'
import { useSortable } from '@dnd-kit/sortable'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import { useEffect, useState } from 'react'
import buildColumns, { FILTERABLE_COLUMN_TO_PARAM } from './Columns'
import configuration from 'modules/configuration'
import { connect } from 'react-redux'

const DraggableHeaderCell = (props) => {
  const { draggable } = props

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: props['data-col-key'],
  })

  if (!draggable) {
    return <th {...props}>{props.children}</th>
  }

  const style = {
    ...props.style,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    cursor: 'move',
  }

  return (
    <th ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {props.children}
    </th>
  )
}

const ApprovalRulesTable = ({
  setMode,
  setSelectedRule,
  setShowCreateModal,
  setDeleteId,
  deleteId,
  filters,
  setFilters,
  filterOptions,
  deleteStrategy,
  tableLoading,
  onRefetch,
  strategiesList,
  editPartialStrategy,
  downloadStrategies,
}) => {
  const { t } = useTranslation()
  const [columns, setColumns] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

  const getDataSource = () => {
    return strategiesList.data.map((item) => {
      const levelsMap = {}
      item.levels?.forEach((level) => {
        const key = `level_${level.position + 1}`
        levelsMap[key] = level.users.map((u) => u.name).join(', ')
      })

      return {
        ...item,
        ...levelsMap,
      }
    })
  }

  const isDraggableColumn = (column) => {
    return !column.fixed
  }

  const onToggle = (id, is_active) => {
    setIsSaving(true)
    editPartialStrategy(id, { is_active: !is_active })
      .then(() => {
        onRefetch()
        message.success(t('FEEDBACK_SAVE_CHANGES_SUCCES'), 8)
        setIsSaving(false)
      })
      .catch((error) => {
        setIsSaving(false)
        notification.error({
          message: t('FEEDBACK_DEFAULT_ERROR'),
          description: error.response.data.message || t('FEEDBACK_SAVE_CHANGES_FAIL'),
          duration: 0,
        })
      })
  }

  const handleDelete = () => {
    setIsSaving(true)
    deleteStrategy(deleteId)
      .then(() => {
        onRefetch()
        message.success(t('FEEDBACK_DELETE_SUCCESS'), 8)
        setIsSaving(false)
        setDeleteId(null)
      })
      .catch((error) => {
        notification.error({
          message: t('FEEDBACK_DEFAULT_ERROR'),
          description: error.response.data.message || t('FEEDBACK_DELETE_FAIL'),
          duration: 0,
        })
        setIsSaving(false)
        setDeleteId(null)
      })
  }

  useEffect(() => {
    setColumns(
      buildColumns({
        t,
        columns: strategiesList.columns,
        onToggle,
        onClickEdit: (rule) => {
          setShowCreateModal(true)
          setSelectedRule(rule)
          setMode('edit')
        },
        setDeleteId,
        deleteId,
        onConfirmDelete: () => handleDelete(),
        isSaving,
        filters,
        filterOptions,
      }),
    )
  }, [deleteId, strategiesList.columns, filters, filterOptions])

  const arrayMove = (array, from, to) => {
    const newArray = [...array]
    const item = newArray.splice(from, 1)[0]
    newArray.splice(to, 0, item)
    return newArray
  }

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return

    setColumns((prev) => {
      const oldData = prev.find((c) => c.key === active.id)
      const newData = prev.find((c) => c.key === over.id)

      if (oldData?.fixed || newData?.fixed) {
        return prev
      }

      const fromIndex = prev.findIndex((c) => c.key === active.id)
      const toIndex = prev.findIndex((c) => c.key === over.id)

      return arrayMove(prev, fromIndex, toIndex)
    })
  }

  const handleTableChange = (_pagination, tableFilters) => {
    const next = {}
    Object.entries(tableFilters || {}).forEach(([columnKey, selected]) => {
      const paramKey = FILTERABLE_COLUMN_TO_PARAM[columnKey]
      if (paramKey && selected && selected.length) {
        next[paramKey] = selected[0]
      }
    })
    setFilters(next)
  }

  return (
    <>
      <ButtonActionBar>
        <Space direction="horizontal">
          <Button.Icon
            title="ACTION_DOWNLOAD"
            icon={<Typography.Icon icon={DownloadOutlined} />}
            onClick={() => downloadStrategies()}
          />
        </Space>
      </ButtonActionBar>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext
          items={columns.map((col) => col.key)}
          strategy={horizontalListSortingStrategy}
        >
          <Table
            size="small"
            columns={columns.map((col) => ({
              ...col,
              onHeaderCell: () => ({
                'data-col-key': col.key,
                'draggable': isDraggableColumn(col),
              }),
            }))}
            loading={tableLoading}
            dataSource={getDataSource()}
            onChange={handleTableChange}
            pagination={{
              total: strategiesList.data.length,
              size: 'small',
              pageSizeOptions: ['10', '20', '30'],
              showSizeChanger: true,
            }}
            scroll={{
              x: calculateScrollX(columns),
            }}
            components={{
              header: {
                cell: DraggableHeaderCell,
              },
            }}
          />
        </SortableContext>
      </DndContext>
    </>
  )
}

const mapStateToProps = (state) => ({
  strategiesList: configuration.selectors.getStrategiesList(state),
})

const mapDispatchToProps = {
  deleteStrategy: configuration.actions.deleteStrategy,
  editPartialStrategy: configuration.actions.editPartialStrategy,
  downloadStrategies: configuration.actions.downloadStrategies,
}

export default connect(mapStateToProps, mapDispatchToProps)(ApprovalRulesTable)
